import Axios from 'axios';
import * as SchoolRequest from '@/models/schoolRequest';
import * as SchoolResponse from '@/models/schoolResponse';
import * as AccountRequest from '@/models/accountRequest';
import * as AccountResponse from '@/models/accountResponse';
import * as GradesCollectionResponse from '@/models/gradesCollectionResponse';
import { Subject } from '@/models/subject';
import { Tag } from '@/models/tag';
import { Person } from '@/models/person';
import { SchoolTypes } from '@/enums/schoolTypes';
import { GradesTypes } from '@/enums/gradesTypes';

export interface Item {
  id: string | number;
  name: string;
}

export interface OrdinarySchool {
  id: string;
  name: string;
  schoolType: SchoolTypes;
}

export interface OtherTest {
  id: string;
  testType: string;
  testTypeName: string;
  schoolType: SchoolTypes;
  fiscalYear: number;
  testTimes: number;
}

interface State {
  ordinarySchoolList: OrdinarySchool[] | null;
  subjectList: Subject[] | null;
  tagList: Tag[] | null;
  tutorList: Person[] | null;
  otherTestList: OtherTest[] | null;
  otherTestTimes: Item[] | null;
}
const mapState: State = {
  ordinarySchoolList: null,
  subjectList: null,
  tagList: null,
  tutorList: null,
  otherTestList: null,
  otherTestTimes: null,
};

const mapActions = {
  async fetchOrdinarySchool({ commit }: any, schoolId: string) {
    const request: SchoolRequest.OrdinarySchoolEditRequest = {
      schoolId,
      schoolName: undefined,
      ownerId: undefined,
      note: undefined,
      paymentMailaddress: undefined,
      subjectList: undefined,
      ordinarySchoolList: undefined,
      keyWord: undefined,
      perPage: undefined,
      page: undefined,
      sort: undefined,
      availableFunctionbit: undefined,
      availableFunctionTypes: undefined,
      reservableMonths: undefined,
      cancellationDays: undefined,
      isAutoEntryOrExit: undefined,
      collectionAgency: undefined,
    };
    const response = await Axios.post('/api/School/OrdinaryList', request).catch((error) => {
      throw error;
    });
    commit('fetchOrdinarySchool', response.data);
  },
  async fetchSubjectAndTag({ commit }: any, schoolId: string) {
    const request: SchoolRequest.SchoolRequest = {
      schoolId,
      schoolName: undefined,
      ownerId: undefined,
      note: undefined,
      paymentMailaddress: undefined,
      subjectList: undefined,
      keyWord: '',
      perPage: 1000000,
      page: 1,
      sort: '',
      availableFunctionbit: undefined,
      availableFunctionTypes: undefined,
      reservableMonths: undefined,
      cancellationDays: undefined,
      isAutoEntryOrExit: undefined,
      collectionAgency: undefined,
    };
    const response = await Axios.post('/api/School/Detail', request).catch((error) => {
      throw error;
    });
    commit('fetchSubjectAndTag', response.data);
  },
  async fetchTutor({ commit }: any, schoolId: string) {
    const request: AccountRequest.TutorAccountRequest = {
      id: undefined,
      userId: undefined,
      lastName: undefined,
      firstName: undefined,
      lastNameKana: undefined,
      firstNameKana: undefined,
      note: undefined,
      subjectIdList: undefined,
      schoolTypeList: undefined,
      authority: undefined,
      keyWord: '',
      perPage: 1000000,
      page: 1,
      sort: '',
    };
    const response = await Axios.post('/api/Account/TutorSearch', request).catch((error) => {
      throw error;
    });
    commit('fetchTutor', response.data);
  },
  async fetchOtherTest({ commit }: any) {
    const response = await Axios.post('/api/GradesCollection/ListView').catch((error) => {
      throw error;
    });
    response.data.gradesCollection.forEach((account: any) => {
      if (typeof response.data.testDay === 'string') {
        response.data.testDay = new Date(response.data.testDay + 'Z');
      }
      if (typeof response.data.lastSendDay === 'string') {
        response.data.lastSendDay = new Date(response.data.lastSendDay + 'Z');
      }
    });
    commit('fetchOtherTest', response.data);
  },
};

const mapMutations = {
  fetchOrdinarySchool(state: State, school: SchoolResponse.OrdinarySchoolEdit) {
    if (!school.ordinarySchoolList) {
      state.ordinarySchoolList = new Array<OrdinarySchool>();
    } else {
      state.ordinarySchoolList = school.ordinarySchoolList.map(
        (ordinarySchool: SchoolResponse.OrdinarySchoolDetail) => {
          return {
            id: ordinarySchool.ordinarySchoolId as string,
            name: ordinarySchool.ordinarySchoolName,
            schoolType: ordinarySchool.schoolType,
          };
        }
      );
    }
  },
  fetchSubjectAndTag(state: State, school: SchoolResponse.SchoolDetailResponse) {
    state.subjectList = !school.subjectList ? new Array<Subject>() : school.subjectList;
    state.tagList = !school.tagList ? new Array<Subject>() : school.tagList;
  },
  fetchTutor(state: State, data: AccountResponse.TutorAccountListResponse) {
    if (!data.accountList) {
      state.tutorList = new Array<Person>();
    } else {
      state.tutorList = data.accountList.map((tutor) => {
        return {
          id: tutor.id,
          name: `${tutor.lastName}　${tutor.firstName}`
        };
      });
    }
  },
  fetchOtherTest(state: State, data: GradesCollectionResponse.GradesCollectionListResponse) {
    if (!data.gradesCollection) {
      state.otherTestList = new Array<OtherTest>();
    } else {
      state.otherTestList = data.gradesCollection
        .filter((item) => item.gradesType === GradesTypes.OtherTest)
        .map((item) => {
          return {
            id: item.gradesCollectionId,
            testType: item.testType,
            testTypeName: item.testTypeName,
            schoolType: item.schoolType,
            fiscalYear: item.fiscalYear,
            testTimes: item.testTimes,
          };
        }
      );
    }
  },
  clearCache(state: State) {
    state.ordinarySchoolList = null;
    state.subjectList = null;
    state.tagList = null;
    state.tutorList = null;
    state.otherTestList = null;
    state.otherTestTimes = null;
  }
};

export default {
  namespaced: true,
  state: mapState,
  actions: mapActions,
  mutations: mapMutations,
};
